// Weekly practice allowance for juniors on a recurring plan.
//
// The packages sell a RATE, not a pot: "1 practice per week" means one per
// week, not four to spend whenever. A month's worth cannot be taken in one
// week, and an unused week does not bank — use it or lose it. That is the
// difference between this and `sessionBalance`, which is a pot and resets on
// the Stripe billing anniversary rather than on any week boundary.
//
// Weeks are the club's existing Sunday-anchored weeks, the same ones member
// court hours reset on (src/utils/weekKey.js). One definition of "this week"
// across the app is worth more than a week that starts on a nicer day.
//
// Usage is DERIVED from attendance rather than kept in a counter. A counter
// has to survive check-in, undo, class cancellation, refunds and athlete
// merges, and each of those is a chance to drift; counting rows cannot drift
// and repairs itself when a record is corrected. `athletes.weeklyBalance`
// exists, is written to 0 in four places and read by nothing — deliberately
// left that way rather than revived.
import { getWeekKey } from './weekKey';

/** Plan ids and slugs that sell a weekly rate, mapped to sessions per week. */
const WEEKLY_RATE_BY_SLUG = {
  '1-practice-weekly': 1,
  '2-session-weekly': 2,
  '3-practices-per-week': 3,
};

/**
 * Sessions per week this plan entitles the athlete to, or null when the plan
 * is not a weekly rate (punchcards, drop-ins, privates — all spend from the
 * pot with no weekly shape).
 *
 * Derived from sessionsIncluded/4 rather than hardcoded counts so a plan added
 * later works without a code change; the slug map is the override for anything
 * whose monthly figure is not a clean multiple.
 */
export function weeklyAllowance(plan) {
  if (!plan || plan.planType !== 'recurring') return null;
  const bySlug = WEEKLY_RATE_BY_SLUG[plan.slug];
  if (bySlug) return bySlug;
  const monthly = Number(plan.sessionsIncluded) || 0;
  if (monthly <= 0) return null;
  // Months are 4-ish weeks; the club sells these as "N per week" and prices
  // them at 4N per month, so this inverts cleanly for any future plan.
  const perWeek = Math.round(monthly / 4);
  return perWeek > 0 ? perWeek : null;
}

/**
 * Attendance rows for one athlete that consumed a session in the given week.
 *
 * Counts a row when it belongs to this athlete and its date falls in the week.
 * Both 'checked_in' and 'reserved' count: reserving spends the session at
 * reserve time (Schedule.js), so a held spot is a used one. Rows with no
 * status are legacy check-ins and count too — they carry checkedInAt and moved
 * the balance. Coach rows carry no athleteId and drop out on their own.
 *
 * A refunded row does NOT count. When a class is cancelled,
 * classRefunds.js gives the session back and stamps refundedAt; the athlete
 * did not get a practice, so the week must be free again too. Without this the
 * refund would return the credit and still burn the week, which is worse for
 * them than not cancelling at all.
 */
export function sessionsUsedInWeek(attendance, athleteId, dateStr) {
  if (!athleteId || !dateStr) return [];
  const week = getWeekKey(dateStr);
  return (attendance || []).filter(r => {
    if (r.athleteId !== athleteId) return false;
    if (!r.date) return false;
    if (r.refundedAt) return false;
    if (r.status === 'cancelled' || r.status === 'canceled') return false;
    return getWeekKey(r.date) === week;
  });
}

/**
 * Whether this athlete may take a session on dateStr, and what it costs them.
 *
 * Order of spend: the week's allowance first, then a makeup credit, then
 * nothing — at which point they are over. A makeup credit deliberately spends
 * ABOVE the weekly cap, because a credit exists to replace a week the club
 * took away and would be unusable if the cap also applied to it.
 *
 * Returns `allowed: false` only as advice. Coaches may check anyone in — the
 * existing rule is that a coach on the sand is not blocked by a billing
 * question — so this reports rather than enforces, and the caller decides.
 */
export function checkWeeklyCap({ plan, attendance, athleteId, dateStr, makeupCredits = [] }) {
  const allowance = weeklyAllowance(plan);
  // No weekly shape: punchcards and drop-ins are governed by sessionBalance
  // alone, so there is nothing here to say about them.
  if (allowance === null) {
    return { applies: false, allowed: true, allowance: null, used: 0, remaining: null,
      usesMakeup: false, availableMakeups: makeupCredits.length, reason: '' };
  }

  const used = sessionsUsedInWeek(attendance, athleteId, dateStr).length;
  const remaining = Math.max(0, allowance - used);
  const available = makeupCredits.filter(c => c.status === 'available').length;

  if (remaining > 0) {
    return { applies: true, allowed: true, allowance, used, remaining,
      usesMakeup: false, availableMakeups: available, reason: '' };
  }
  if (available > 0) {
    return { applies: true, allowed: true, allowance, used, remaining: 0,
      usesMakeup: true, availableMakeups: available,
      reason: `Weekly allowance used (${used} of ${allowance}). This will spend a makeup credit.` };
  }
  return { applies: true, allowed: false, allowance, used, remaining: 0,
    usesMakeup: false, availableMakeups: 0,
    reason: `Already had ${used} of ${allowance} practice${allowance === 1 ? '' : 's'} this week, `
      + 'and has no makeup credits.' };
}

/**
 * Practice SLOTS the club actually delivered in a given week, as "date startTime".
 *
 * Slots, not classes. The club runs an overflow court beside the main one —
 * "Torch Junior Beginner" on court 4 alongside "Torch Junior Intermediate" on
 * court 3, same 17:45 slot — and folds the overflow into the main court when
 * few players turn up. That cancellation is a consolidation, not a lost
 * practice: everyone still trained. Counting class documents would read it as
 * the week halving; counting distinct time slots reads it correctly, and does
 * so without depending on anyone picking the right cancel reason from a
 * dropdown.
 *
 * The whole week counts, not just what is still to come. A practice on Monday
 * that the athlete skipped was still delivered — the club owes nothing for a
 * session it put on and they chose to miss. That is what use-it-or-lose-it
 * means.
 */
export function slotsDeliveredInWeek(classes, weekOfDate, sessionType) {
  if (!weekOfDate) return [];
  const week = getWeekKey(weekOfDate);
  const slots = new Set();
  (classes || []).forEach(c => {
    if (!c.date) return;
    if (c.status === 'canceled' || c.status === 'cancelled') return;
    if (sessionType && c.sessionType !== sessionType) return;
    if (getWeekKey(c.date) !== week) return;
    slots.add(`${c.date} ${c.startTime || ''}`);
  });
  return [...slots].sort();
}

/**
 * How many makeup credits an athlete is owed for a week.
 *
 * The club's obligation is to OFFER the plan's rate — one practice a week, or
 * two. What the athlete then did with those slots is their business: a week
 * they skipped is lost, not owed. So this compares the promise against what
 * was delivered, and never looks at attendance at all.
 *
 *   owed = allowance − slots delivered that week
 *                    − credits already granted for that week
 *
 * The last term caps the WEEK rather than the class. Two practices in one week
 * both cancelled would otherwise each see an empty week and each grant a full
 * allowance, handing a 2x athlete four credits for a week they lost two. It
 * also makes the incremental case correct: cancelling the overflow court owes
 * nothing while the main court still runs, and cancelling the main court
 * afterwards tops up the difference.
 *
 * Floored at zero, and zero for anyone not on a weekly-rate plan: punchcard
 * holders are made whole by the session refund, which is a different mechanism
 * and already works.
 */
export function makeupCreditsOwed({ plan, classes, cancelledClass, creditsAlreadyGranted = 0 }) {
  const allowance = weeklyAllowance(plan);
  if (allowance === null || !cancelledClass?.date) return 0;
  const delivered = slotsDeliveredInWeek(classes, cancelledClass.date, cancelledClass.sessionType).length;
  return Math.max(0, allowance - delivered - creditsAlreadyGranted);
}

/**
 * The makeup credit to spend next: oldest first, so a credit granted for a
 * rained-out practice in March is used before one from last week and nothing
 * ages out unnoticed.
 */
export function nextMakeupCredit(makeupCredits = []) {
  return (makeupCredits || [])
    .filter(c => c.status === 'available')
    .sort((a, b) => (a.grantedAt?.seconds || 0) - (b.grantedAt?.seconds || 0))[0] || null;
}
